import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { OptionSelect } from "@/components/data/FieldInput";
import { StatusBadge } from "@/components/data/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtMoney, fmtNum } from "@/lib/api";
import { exportExcel, exportPDF } from "@/lib/export";

const NIVEAUX = [
  { value: "ville", label: "Par ville" }, { value: "zone", label: "Par zone" }, { value: "superviseur", label: "Par superviseur" },
  { value: "revendeur", label: "Détail par revendeur" }, { value: "tpe", label: "Détail par TPE" },
];
const m = (k, label) => ({ k, label, money: true });
const GROUP_COLS = [
  { k: "nb_revendeurs", label: "Revendeurs" }, { k: "nb_tpe", label: "TPE" }, m("dette_anterieure", "Dette antérieure"),
  m("ca", "CA du jour"), m("gains", "Gains"), m("commission", "Commissions"), m("du", "Montant attendu"),
  m("encaisse", "Encaissé"), m("reste", "Reste dû"), m("ecart", "Écart de caisse"),
];
const TPE_COLS = [
  { k: "revendeur_label", label: "Revendeur", text: true }, { k: "superviseur_label", label: "Superviseur", text: true },
  m("loto_ca", "Loto CA"), m("loto_gains", "Loto gains"), m("hippique_ca", "Hipp. CA"), m("hippique_gains", "Hipp. gains"),
  m("sport_ca", "Sport CA"), m("sport_gains", "Sport gains"), m("total_ca", "CA"), m("total_gains", "Gains"),
  m("total_solde", "Solde"), m("commission", "Commission"), m("montant_du", "Montant dû"),
];

export function RecapTable({ journeeId, reloadKey = 0, initial = "superviseur" }) {
  const { meta, can } = useAuth();
  const [niveau, setNiveau] = useState(initial);
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!journeeId) return;
    api.get(`/tfj/journees/${journeeId}/recap`, { params: { niveau } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [journeeId, niveau, reloadKey]);
  if (!d) return <div className="text-sm text-muted-foreground p-4">Chargement…</div>;
  const isTpe = niveau === "tpe";
  const cols = isTpe ? TPE_COLS : [...(niveau === "revendeur" ? [{ k: "superviseur_label", label: "Superviseur", text: true }] : []), ...GROUP_COLS];
  const first = { label: isTpe ? "TPE" : NIVEAUX.find((n) => n.value === niveau).label.replace(/^(Par|Détail par) /, "").replace(/^./, (c) => c.toUpperCase()), raw: (r) => (isTpe ? r.tpe_identifiant : r.libelle) };
  const exportCols = [first, ...cols.map((c) => ({ label: c.label, raw: (r) => (c.text ? r[c.k] : Number(r[c.k] || 0)) })), ...(isTpe ? [] : [{ label: "État", raw: (r) => r.etat }])];
  const title = `Récapitulatif TFJ ${d.journee.date} – ${NIVEAUX.find((n) => n.value === niveau).label}`;
  const totalFor = (k) => (isTpe ? d.rows.reduce((s, r) => s + Number(r[k] || 0), 0) : d.totals[k] ?? d.rows.reduce((s, r) => s + Number(r[k] || 0), 0));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3" data-testid="recap-activites">
        {d.activites.map((a) => (
          <div key={a.activite} className="p-3 rounded-lg border bg-card">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{a.activite}</div>
            <div className="font-mono text-sm font-bold mt-1">{fmtMoney(a.ca)}</div>
            <div className="text-[11px] text-muted-foreground">Gains {fmtNum(a.gains)} · Solde {fmtNum(a.solde)}</div>
          </div>
        ))}
      </div>
      <div className="bg-card border rounded-lg">
        <div className="p-3 border-b flex flex-wrap gap-2 items-center">
          <div className="w-full sm:w-56"><OptionSelect options={NIVEAUX} value={niveau} onChange={(v) => v && setNiveau(v)} testId="recap-niveau-select" /></div>
          {can("tfj", "exporter") && (
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={() => exportExcel(title, exportCols, d.rows)} data-testid="recap-export-excel"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
              <Button variant="outline" size="sm" onClick={() => exportPDF(title, exportCols, d.rows, { company: meta.settings?.nom_entreprise, footer: meta.settings?.pied_de_page_rapports })} data-testid="recap-export-pdf"><FileText className="h-4 w-4 mr-1" />PDF</Button>
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="recap-table">
            <thead className="bg-muted/60 uppercase tracking-wider text-muted-foreground text-[10px]">
              <tr>
                <th className="px-3 py-2.5 text-left">{first.label}</th>
                {cols.map((c) => <th key={c.k} className={`px-3 py-2.5 whitespace-nowrap ${c.text ? "text-left" : "text-right"}`}>{c.label}</th>)}
                {!isTpe && <th className="px-3 py-2.5 text-left">État</th>}
              </tr>
            </thead>
            <tbody>
              {d.rows.map((r, i) => (
                <tr key={r.id || r.cle || i} className="border-t hover:bg-accent/40" data-testid="recap-row">
                  <td className="px-3 py-2 font-semibold whitespace-nowrap">{first.raw(r)}</td>
                  {cols.map((c) => (
                    <td key={c.k} className={`px-3 py-2 whitespace-nowrap ${c.text ? "" : "text-right font-mono"} ${!c.text && Number(r[c.k]) < 0 ? "text-primary" : ""}`}>
                      {c.text ? r[c.k] || "—" : c.money ? fmtNum(r[c.k]) : r[c.k]}
                    </td>
                  ))}
                  {!isTpe && <td className="px-3 py-2"><StatusBadge value={r.etat} /></td>}
                </tr>
              ))}
              {!d.rows.length && <tr><td colSpan={cols.length + 2} className="py-10 text-center text-muted-foreground">Aucune donnée</td></tr>}
            </tbody>
            {d.rows.length > 0 && (
              <tfoot className="border-t-2 border-foreground/80 bg-muted/40 font-semibold">
                <tr>
                  <td className="px-3 py-2">Total</td>
                  {cols.map((c) => <td key={c.k} className="px-3 py-2 text-right font-mono whitespace-nowrap">{c.text ? "" : c.money ? fmtNum(totalFor(c.k)) : fmtNum(totalFor(c.k))}</td>)}
                  {!isTpe && <td />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground">Montants en {meta.settings?.symbole_devise || "GNF"}. Montant attendu = Solde (CA − Gains) − Commission revendeur. Reste dû = Attendu + Dette antérieure − Encaissé. Écart de caisse = Encaissé − Attendu.</p>
    </div>
  );
}
